import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@supabase/auth-helpers-react";

/**
 * Lightweight hook that checks whether the current user has at least
 * one certificate (either from study tracks or from coding challenges).
 * Uses HEAD-only count queries so payload is zero.
 */
export function useHasCertificates() {
  const user = useUser();

  return useQuery({
    queryKey: ["has-certificates", user?.id],
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (!user?.id) return { hasAny: false, trackCount: 0, challengeCount: 0 };

      const [{ count: trackCount }, { count: challengeCount }] = await Promise.all([
        supabase
          .from("user_certificates")
          .select("*", { count: "exact", head: true })
          .eq("user_id", user.id),
        supabase
          .from("challenge_certificates")
          .select("*", { count: "exact", head: true })
          .eq("user_id", user.id),
      ]);

      const total = (trackCount || 0) + (challengeCount || 0);
      return {
        hasAny: total > 0,
        trackCount: trackCount || 0,
        challengeCount: challengeCount || 0,
      };
    },
  });
}
